import React, { useMemo } from "react";
import {
	Alert,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { summarizeRolesForCourses } from "./mixedRoleSafeguards";

function formatCourseTitle(course) {
	const courseCode = [course?.subject_id, course?.cnumber].filter(Boolean).join(" ");
	const title = course?.ctitle || "Untitled course";
	const classId = course?.classid ? `Class ID ${course.classid}` : "No class ID";

	return [courseCode, title, classId].filter(Boolean).join(" - ");
}

export default function MixedRoleSubmitDialog({
	open,
	courses,
	onCancel,
	onContinue,
	onRemoveRole,
	isSubmitting,
}) {
	const mixedCourses = useMemo(() => courses || [], [courses]);
	const roleSummaries = useMemo(() => summarizeRolesForCourses(mixedCourses), [mixedCourses]);
	const courseCount = mixedCourses.length;

	return (
		<Dialog open={open} onClose={isSubmitting ? undefined : onCancel} fullWidth maxWidth="xl">
			<DialogTitle className="flex items-center gap-2">
				<WarningAmberIcon color="warning" />
				Mixed TA and non-TA roles detected
			</DialogTitle>
			<DialogContent dividers>
				<Alert severity="warning" sx={{ mb: 2 }}>
					{courseCount === 1 ? "This course has" : `${courseCount} courses have`} both TA and
					non-TA instructors. Continue only when those roles should share this survey.
				</Alert>

				<div className="grid gap-3 xl:grid-cols-2">
					<div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
						<p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
							Affected courses
						</p>
						<div className="mt-2 flex max-h-56 flex-col gap-2 overflow-y-auto pr-1">
							{mixedCourses.map((course) => (
								<div
									key={course.classid}
									className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
								>
									<p className="font-semibold text-gray-900">{formatCourseTitle(course)}</p>
									<p className="mt-1 text-xs text-gray-600">
										{(course.roles || [])
											.map(
												(role) =>
													`${role.name || "Unknown instructor"} (${role.class_role || "N/A"})`,
											)
											.join(", ")}
									</p>
								</div>
							))}
						</div>
					</div>

					<div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
						<p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
							Remove a role before submitting
						</p>
						<p className="mt-1 text-xs text-gray-600">
							Removing a role updates the current course list and returns you to review.
						</p>
						<div className="mt-2 flex max-h-56 flex-col gap-2 overflow-y-auto pr-1">
							{roleSummaries.map((roleSummary) => (
								<div
									key={roleSummary.roleId}
									className="flex flex-col gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 xl:flex-row xl:items-center xl:justify-between"
								>
									<div className="min-w-0">
										<p className="font-semibold text-gray-900">
											{roleSummary.roleId} - {roleSummary.roleDescription}
										</p>
										<p className="text-xs text-gray-600">
											{roleSummary.instructorCount} instructor
											{roleSummary.instructorCount === 1 ? "" : "s"} across{" "}
											{roleSummary.courseCount} course
											{roleSummary.courseCount === 1 ? "" : "s"}
										</p>
									</div>
									<Button
										variant="outlined"
										color="error"
										disabled={isSubmitting}
										onClick={() => onRemoveRole(roleSummary.roleId)}
										sx={{ minWidth: 132 }}
									>
										<DeleteForeverIcon sx={{ mr: 1 }} />
										Remove
									</Button>
								</div>
							))}
						</div>
					</div>
				</div>
			</DialogContent>
			<DialogActions sx={{ justifyContent: "space-between", p: 2 }}>
				<Button onClick={onCancel} disabled={isSubmitting} variant="outlined">
					Cancel
				</Button>
				<Button onClick={onContinue} disabled={isSubmitting} variant="contained" color="warning">
					Continue Anyway
				</Button>
			</DialogActions>
		</Dialog>
	);
}
